// Driver entity types

import type { UUID, ISODateString, ISODateTimeString, Address, Contact, Timestamps, TenantEntity } from './common';
import type { DriverStatus, CDLClass, CDLEndorsement, HOSStatus } from './enums';
import { DriverType, DriverTaxClassification, PayType, DriverRateAddOnType } from './enums';
import type { VehicleSummary } from './load';

/** CDL information */
export interface CDLInfo {
  number: string;
  state: string;
  class: CDLClass;
  expirationDate: ISODateString;
  endorsements: CDLEndorsement[];
  restrictions?: string[];
}

/** Medical card information */
export interface MedicalCard {
  number?: string;
  expirationDate: ISODateString;
  examinerName?: string;
  examDate?: ISODateString;
  status?: string;
}

/** Passport information */
export interface PassportInfo {
  number?: string;
  country?: string;
  expiration?: ISODateString;
}

/** TWIC card information */
export interface TWICInfo {
  number?: string;
  expiration?: ISODateString;
}

/** Driver credentials */
export interface DriverCredentials {
  cdl?: {
    number?: string;
    state?: string;
    class?: string;
    issueDate?: ISODateString;
    expirationDate?: ISODateString;
    status?: string;
  };
  endorsements?: string[];
  restrictions?: string[];
  equipmentCertifications?: string[];
  medicalCard?: MedicalCard;
  passport?: PassportInfo;
  twic?: TWICInfo;
}

/** Compliance expiring item */
export interface ComplianceExpiringItem {
  itemType: string;
  expirationDate: ISODateString;
  daysRemaining: number;
  status: string;
  driverId?: string;
  driverName?: string;
}

/** Driver compliance status */
export interface DriverComplianceStatus {
  overallStatus?: string;
  cdlStatus?: string;
  medicalStatus?: string;
  drugTestStatus?: string;
  mvrStatus?: string;
  trainingStatus?: string;
  expiringItems?: ComplianceExpiringItem[];
  missingDocuments?: string[];
}

/** HOS (Hours of Service) summary */
export interface HOSSummary {
  status: HOSStatus;
  lastStatusChange: ISODateTimeString;
  drivingRemaining: number;   // Minutes
  dutyRemaining: number;      // Minutes
  cycleRemaining: number;     // Minutes
  breakTaken: boolean;
  lastRestartDate?: ISODateString;
}

/** Driver pay configuration */
export interface PayConfig {
  type: PayType;
  rate: number;
  minimumGuarantee?: number;
  perDiem?: number;
}

/**
 * Driver rate add-on configuration
 * Examples: flat fee per load, extra stop fee, detention pay
 */
export interface DriverRateAddOn {
  id?: string;
  type: DriverRateAddOnType;
  name: string;                    // Display name (e.g., "Extra Stop Fee")
  amount: number;                  // Amount in cents
  isPercentage?: boolean;          // If true, amount is a percentage (for percentage-based add-ons)
  isActive: boolean;
  description?: string;
}

/**
 * Complete driver rate configuration
 * Supports both Company Driver (per mile) and Owner Operator (percentage) pay structures
 */
export interface DriverRateConfig {
  // Base pay configuration
  payType: PayType;                // per_mile, percentage, hourly, salary, flat
  payRate: number;                 // Rate amount (cents for per_mile, basis points for percentage, e.g., 8800 = 88%)

  // Tax classification
  taxClassification: DriverTaxClassification;  // w2 or 1099

  // Fuel deduction settings (primarily for Owner Operators)
  fuelDeductionEnabled: boolean;
  fuelDeductionPercentage?: number;  // Percentage of fuel to deduct (e.g., 100 = 100%)

  // Rate add-ons
  addOns: DriverRateAddOn[];

  // Minimums and guarantees
  minimumPayPerLoad?: number;      // Minimum pay per load in cents
  minimumPayPerWeek?: number;      // Minimum weekly guarantee in cents

  // Per diem
  perDiemEnabled: boolean;
  perDiemRate?: number;            // Daily per diem amount in cents
}

/**
 * Default rate configurations by driver type
 */
export const DEFAULT_RATE_CONFIGS: Record<DriverType, Partial<DriverRateConfig>> = {
  [DriverType.COMPANY]: {
    payType: PayType.PER_MILE,
    taxClassification: DriverTaxClassification.W2,
    fuelDeductionEnabled: false,
    addOns: [],
    perDiemEnabled: false,
  },
  [DriverType.OWNER_OPERATOR]: {
    payType: PayType.PERCENTAGE,
    taxClassification: DriverTaxClassification.C1099,
    fuelDeductionEnabled: true,
    fuelDeductionPercentage: 100,
    addOns: [],
    perDiemEnabled: false,
  },
  [DriverType.LEASE]: {
    payType: PayType.PERCENTAGE,
    taxClassification: DriverTaxClassification.C1099,
    fuelDeductionEnabled: true,
    fuelDeductionPercentage: 100,
    addOns: [],
    perDiemEnabled: false,
  },
};

/** Driver location */
export interface DriverLocation {
  latitude: number;
  longitude: number;
  heading?: number;
  speed?: number;
  location?: string;
  timestamp: ISODateTimeString;
}

/** Load summary for driver references */
export interface LoadSummary {
  id: UUID;
  loadNumber: string;
  originCity: string;
  originState: string;
  destinationCity: string;
  destinationState: string;
  pickupDate: ISODateString;
  deliveryDate: ISODateString;
}

/** Active load summary - lightweight load info for driver lists */
export interface ActiveLoadSummary {
  id: UUID;
  loadNumber: string;
  customerId?: UUID;
  originCity?: string;
  originState?: string;
  destinationCity?: string;
  destinationState?: string;
  equipmentType?: string;
  status?: string;
}

/** Full driver entity */
export interface Driver extends Timestamps, TenantEntity {
  id: UUID;
  driverNumber: string;
  status: DriverStatus;
  type: DriverType;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address?: Address;
  dateOfBirth?: ISODateString;
  avatarUrl?: string;
  emergencyContact?: Contact & { relationship?: string };
  hireDate: ISODateString;
  terminationDate?: ISODateString;
  cdl: CDLInfo;
  medicalCard: MedicalCard;
  pay: PayConfig;
  currentVehicleId?: UUID;
  currentVehicle?: VehicleSummary;
  currentTrailerId?: UUID;
  currentLoadId?: UUID;
  currentLoad?: LoadSummary;
  hos?: HOSSummary;
  location?: DriverLocation;
  totalMiles?: number;
  totalLoads?: number;
  avgRating?: number;
  onTimePercentage?: number;
  performance?: DriverPerformance;
  // New fields from API
  credentials?: DriverCredentials;
  compliance?: DriverComplianceStatus;
  invitationAccepted?: boolean;
  relayDriverId?: string;
  ssnLast4?: string;
  locationCity?: string;
  locationState?: string;
  locationLatitude?: number;
  locationLongitude?: number;
  locationUpdatedAt?: ISODateTimeString;
  // New fields from client document
  bankName?: string;
  accountNumber?: string;
  nameOnAccount?: string;
  accountType?: string;
  routingNumber?: string;
  taxId?: string;
  taxIdType?: string;
  businessName?: string;
  fleet?: string;
  fuelCards?: string[];
  flatFee?: number;
  extraStopRate?: number;
}

/** Driver performance */
export interface DriverPerformance {
  totalMiles?: number;
  totalLoads?: number;
  avgRating?: number;
  onTimePercentage?: number;
  // New fields from API
  fuelMpg?: number;
  loads30Days?: number;
  miles30Days?: number;
  onTimePct?: number;
  safetyScore?: number;
}

/** Driver list item */
export interface DriverListItem {
  id: UUID;
  driverNumber: string;
  firstName: string;
  lastName: string;
  status: DriverStatus;
  type: DriverType;
  phone: string;
  email: string;
  cdlNumber?: string;
  cdlState?: string;
  cdlClass?: CDLClass;
  cdlEndorsements?: CDLEndorsement[];
  cdlExpiration?: ISODateString;
  medicalCardNumber?: string;
  medicalExpiration?: ISODateString;
  currentLoadNumber?: string;
  currentLoadId?: UUID;
  hosStatus?: HOSStatus;
  hosDrivingRemaining?: number;
  locationCity?: string;
  locationState?: string;
  locationLatitude?: number;
  locationLongitude?: number;
  locationUpdatedAt?: ISODateTimeString;
  avatarUrl?: string;
  performance?: DriverPerformance;
  currentLocation?: DriverLocation;
  // Pay configuration
  payType?: PayType;
  payRate?: number;
  minimumGuarantee?: number;
  perDiem?: number;
  // New fields from API
  dateOfBirth?: ISODateString;
  hireDate?: ISODateString;
  terminationDate?: ISODateString;
  address?: Address;
  emergencyContact?: Contact & { relationship?: string };
  credentials?: DriverCredentials;
  compliance?: DriverComplianceStatus;
  invitationAccepted?: boolean;
  relayDriverId?: string;
  ssnLast4?: string;
  taxClassification?: string;
  bankName?: string;
  accountNumber?: string;
  nameOnAccount?: string;
  accountType?: string;
  routingNumber?: string;
  taxId?: string;
  taxIdType?: string;
  businessName?: string;
  fleet?: string;
  fuelCards?: string[];
  flatFee?: number;
  extraStopRate?: number;
  activeLoads?: ActiveLoadSummary[];
  // Communication preferences
  commPrefEmail?: boolean;
  commPrefPush?: boolean;
  // Availability fields (from get available drivers endpoint)
  hosAvailableHours?: number;
  distanceToPickupMiles?: number;
  equipmentMatch?: boolean;
  homeTimeDue?: string;
  nextAvailable?: string;
  preferenceMatchScore?: number;
}

/** Create driver request */
export interface CreateDriverRequest {
  type: DriverType;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address?: Omit<Address, 'latitude' | 'longitude'>;
  dateOfBirth?: ISODateString;
  hireDate: ISODateString;
  cdl: {
    number: string;
    state: string;
    class: CDLClass;
    expirationDate: ISODateString;
    endorsements?: CDLEndorsement[];
  };
  medicalCard: {
    expirationDate: ISODateString;
    number?: string;
  };
  pay: PayConfig;
  emergencyContact?: Contact & { relationship?: string };
}

/** Driver filter form */
export interface DriverFilters {
  status?: DriverStatus[];
  type?: DriverType[];
  hosStatus?: HOSStatus[];
  cdlClass?: CDLClass[];
  hasLoad?: boolean;
  search?: string;
}

/** HOS Clock entry - point-in-time snapshot of driver's HOS availability */
export interface HOSClockEntry {
  dutyStatus: string;
  breakRemainingMs?: number;
  driveRemainingMs?: number;
  shiftRemainingMs?: number;
  cycleRemainingMs?: number;
  cycleTomorrowMs?: number;
  cycleViolationMs?: number;
  shiftDrivingViolationMs?: number;
  samsaraVehicleId?: string;
  vehicleName?: string;
  source: string;
  recordedAt: string;
}

/** HOS Clock history response */
export interface HOSClockHistoryResponse {
  entries: HOSClockEntry[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

/** HOS Clock latest response */
export interface HOSClockLatestResponse {
  entry: HOSClockEntry | null;
}

// ---------------------------------------------------------------------------
// Recurring Charges (Deductions & Bonuses)
// ---------------------------------------------------------------------------

/** Recurring charge type */
export type RecurringChargeType = 'deduction' | 'bonus';

/** Recurring charge frequency */
export type RecurringChargeFrequency = 'per_paystub' | 'per_month' | 'once';

/** Recurring charge status */
export type RecurringChargeStatus = 'active' | 'completed' | 'inactive';

/** Recurring charge entity */
export interface RecurringCharge {
  unid: string;
  type: RecurringChargeType;
  category: string;
  description: string;
  amount: number;
  frequency: RecurringChargeFrequency;
  status: RecurringChargeStatus;
  effectiveDate: ISODateString | null;
  endDate: ISODateString | null;
  createdAt: ISODateTimeString;
  updatedAt: ISODateTimeString;
  usageCount: number;
  lastAppliedAt: ISODateTimeString | null;
}

/** Create recurring charge request */
export interface CreateRecurringChargeRequest {
  type: RecurringChargeType;
  category: string;
  description: string;
  amount: number;
  frequency: RecurringChargeFrequency;
  effectiveDate?: string;
  endDate?: string;
}

/** Update recurring charge request */
export interface UpdateRecurringChargeRequest {
  category?: string;
  description?: string;
  amount?: number;
  frequency?: RecurringChargeFrequency;
  status?: RecurringChargeStatus;
  effectiveDate?: string;
  endDate?: string;
}
